import { useState, useMemo } from 'react';
import type { Agent } from '../components/LeftSidebar';
import type { AgentType } from '../types/chat';
import { useEllisDTFlag } from './useEllisDTFlag';

const AGENT_IMAGES = [
  'https://cdn.builder.io/api/v1/image/assets%2Fa9b93fe44e0741148c7d9f968e073a29%2F5a73811ed4b14b9babf7ff25a9a1d20a?format=webp&width=800&height=1200',
  'https://cdn.builder.io/api/v1/image/assets%2Fa9b93fe44e0741148c7d9f968e073a29%2F6b4d319aef9b4d1f967949947a247894?format=webp&width=800&height=1200',
  'https://cdn.builder.io/api/v1/image/assets%2Fa9b93fe44e0741148c7d9f968e073a29%2F914df02e418a4b09bb336bb4172c9d8f?format=webp&width=800&height=1200',
  'https://cdn.builder.io/api/v1/image/assets%2Fa9b93fe44e0741148c7d9f968e073a29%2F17c33b1f6ea9493a8eae404c6b08a4b5?format=webp&width=800&height=1200',
  'https://cdn.builder.io/api/v1/image/assets%2Fa9b93fe44e0741148c7d9f968e073a29%2F515b85c5d41a44d290666d44cd94f6fe?format=webp&width=800&height=1200',
  'https://cdn.builder.io/api/v1/image/assets%2Fa9b93fe44e0741148c7d9f968e073a29%2F8de9b254d28e4e659b56de5c193cd4ce?format=webp&width=800&height=1200',
  'https://cdn.builder.io/api/v1/image/assets%2Fa9b93fe44e0741148c7d9f968e073a29%2Fda69f6995e71497ca1a06eafb2a6696f?format=webp&width=800&height=1200',
  'https://cdn.builder.io/api/v1/image/assets%2Fa9b93fe44e0741148c7d9f968e073a29%2F3722e44f750a4e70bb2ff33ca41bb025?format=webp&width=800&height=1200',
];

const AGENTS: Agent[] = [
  // {
  //   id: "general",
  //   name: "General Chat",
  //   description: "General purpose AI assistant powered by Solace Agent Mesh",
  //   avatar: "GC",
  //   avatarBg: "#003F2D",
  //   avatarImage: AGENT_IMAGES[0],
  // },
  // {
  //   id: "planner",
  //   name: "Planner Agent",
  //   description: "Helps with planning and organizing tasks",
  //   avatar: "PA",
  //   avatarBg: "#1E5A7A",
  //   avatarImage: AGENT_IMAGES[1],
  // },
  // {
  //   id: "qe",
  //   name: "QE Agent",
  //   description: "Quality engineering and testing assistance",
  //   avatar: "QE",
  //   avatarBg: "#7A4C2A",
  //   avatarImage: AGENT_IMAGES[2],
  // },
  // {
  //   id: 'sprint-summary',
  //   name: 'Sprint Summary Agent',
  //   description: 'Creates sprint summaries and reports',
  //   avatar: 'SS',
  //   avatarBg: '#5A3A7A',
  //   avatarImage: AGENT_IMAGES[3],
  // },
  // {
  //   id: 'github-code',
  //   name: 'Github Code MCP',
  //   description: 'GitHub code repository integration',
  //   avatar: 'GH',
  //   avatarBg: '#333333',
  //   avatarImage: AGENT_IMAGES[4],
  // },
  {
    id: 'jira-agile',
    name: 'Chat w/ Jira',
    description: 'Jira agile project management',
    avatar: 'JA',
    avatarBg: '#0052CC',
    avatarImage: AGENT_IMAGES[5],
  },
  {
    id: 'ado-agile',
    name: 'Chat w/ ADO(Agile)',
    description:
      'Azure DevOps sprint & backlog management - powered by Solace ADO Agile Orchestrator',
    avatar: 'AA',
    avatarBg: '#0078D4',
    avatarImage: AGENT_IMAGES[6],
  },
  {
    id: 'knowledge-graph',
    name: 'Chat w/ APM&Gates',
    description:
      'CBRE enterprise knowledge discovery and organizational insights',
    avatar: 'AC',
    avatarBg: '#0078D4',
    avatarImage: AGENT_IMAGES[4],
  },
  // {
  //   id: 'ado-code',
  //   name: 'ADO Code MCP',
  //   description:
  //     'Azure DevOps CI/CD pipelines & repositories - powered by Solace ADO Code Orchestrator',
  //   avatar: 'AC',
  //   avatarBg: '#0078D4',
  //   avatarImage: AGENT_IMAGES[7],
  // },
];

/**
 * Map UI agent IDs to their corresponding Solace agent names
 */
export const getSolaceAgentName = (agentId: AgentType, llm: string): string => {
  switch (agentId) {
    case 'general':
      return `OrchestratorAgent`;
    //  return `OrchestratorAgent_${llm}`;
    case 'ado-code':
      return `ADOCodeOrchestratorAgent_${llm}`;
    case 'ado-agile':
      return `ADOAgileOrchestratorAgent_${llm}`;
    default:
      return `OrchestratorAgent_${llm}`;
  }
};

export function useAgents() {
  const { knowledgeGraphEnabled } = useEllisDTFlag();
  const [currentAgent, setCurrentAgent] = useState<AgentType>('jira-agile');
  const [selectedAgents, setSelectedAgents] = useState<Set<AgentType>>(
    new Set(),
  );
  const [selectedLLM, setSelectedLLM] = useState<'GPT' | 'Claude' | 'Gemini'>(
    'Claude',
  );
  const [showAgentMultiselect, setShowAgentMultiselect] = useState(false);

  const currentAgentName = useMemo(() => {
    const primaryAgent =
      AGENTS.find(a => a.id === currentAgent)?.name || 'Assistant';
    if (selectedAgents.size === 0) {
      return primaryAgent;
    }
    const selectedAgentNames = Array.from(selectedAgents)
      .map(id => AGENTS.find(a => a.id === id)?.name)
      .filter(Boolean)
      .join(' + ');
    return `${primaryAgent} + ${selectedAgentNames}`;
  }, [currentAgent, selectedAgents]);

  const handleAgentSelection = (agentId: AgentType) => {
    const newSelected = new Set(selectedAgents);
    if (newSelected.has(agentId)) {
      newSelected.delete(agentId);
    } else {
      newSelected.add(agentId);
    }
    setSelectedAgents(newSelected);
  };

  const agents = knowledgeGraphEnabled
    ? AGENTS
    : AGENTS.filter(a => a.id !== 'knowledge-graph');

  return {
    agents,
    agentImages: AGENT_IMAGES,
    currentAgent,
    setCurrentAgent,
    selectedAgents,
    setSelectedAgents,
    selectedLLM,
    setSelectedLLM,
    showAgentMultiselect,
    setShowAgentMultiselect,
    currentAgentName,
    handleAgentSelection,
    getSolaceAgentName,
  };
}
